import { useIntl } from '@/i18n';
import {
  getAdminInboxDetail,
  markAdminInboxRead,
  pageAdminInbox,
} from '@/services/muou-admin-inbox';
import type { AdminInboxDetailVO, AdminInboxPageVO } from '@/types';
import { BellOutlined } from '@ant-design/icons';
import { Badge, Button, Modal, Pagination, Popover, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const PAGE_SIZE = 10;

/** 后台帐号专属收件入口；未读总数始终使用服务端全量事实。 */
export default function AdminInboxBell() {
  const intl = useIntl();
  const t = (key: string) => intl.formatMessage({ id: `adminInbox.${key}` });
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AdminInboxPageVO>();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<AdminInboxDetailVO>();
  const [opening, setOpening] = useState(false);
  const [reading, setReading] = useState(false);
  const load = async (current: number) => {
    setLoading(true);
    try {
      const result = await pageAdminInbox({ page: current, size: PAGE_SIZE });
      setData(result);
      setPage(current);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(1).catch(() => undefined);
  }, [intl.locale]);
  const openMessage = async (deliveryId: string) => {
    if (opening) return;
    setOpening(true);
    try {
      setDetail(await getAdminInboxDetail(deliveryId));
      setOpen(false);
    } finally {
      setOpening(false);
    }
  };
  const markRead = async () => {
    if (!detail || reading) return;
    setReading(true);
    try {
      await markAdminInboxRead(detail.deliveryId);
      setDetail(await getAdminInboxDetail(detail.deliveryId));
      await load(1);
    } finally {
      setReading(false);
    }
  };
  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (value) void load(1).catch(() => undefined);
        }}
        title={t('title')}
        content={
          <Spin spinning={loading || opening}>
            <div className="w-80 max-w-full">
              {data?.rows.map((row) => (
                <div
                  key={row.deliveryId}
                  className="border-b border-neutral-200 py-2"
                >
                  <Button
                    type="link"
                    className="h-auto whitespace-normal px-0 text-left"
                    onClick={() => {
                      void openMessage(row.deliveryId).catch(() => undefined);
                    }}
                  >
                    {row.title}
                  </Button>
                  <div className="text-secondary text-xs">
                    {dayjs(row.receivedAt).format('YYYY-MM-DD HH:mm')}
                  </div>
                </div>
              ))}
              {data && data.total > PAGE_SIZE && (
                <Pagination
                  className="mt-3"
                  simple
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={data.total}
                  onChange={(current) => {
                    void load(current).catch(() => undefined);
                  }}
                />
              )}
            </div>
          </Spin>
        }
      >
        <Button
          type="text"
          aria-label={t('title')}
          icon={
            <Badge count={data?.unreadCount} size="small">
              <BellOutlined />
            </Badge>
          }
        />
      </Popover>
      <Modal
        open={!!detail}
        title={detail?.title}
        onCancel={() => setDetail(undefined)}
        footer={
          <Space>
            {detail?.targetUrl && (
              <Button href={detail.targetUrl}>{t('openTarget')}</Button>
            )}
            <Button
              loading={reading}
              disabled={!detail || detail.readState === 'read'}
              onClick={() => {
                void markRead().catch(() => undefined);
              }}
            >
              {t('markRead')}
            </Button>
            <Button onClick={() => setDetail(undefined)}>
              {intl.formatMessage({ id: 'common.close' })}
            </Button>
          </Space>
        }
      >
        {detail && (
          <>
            <div className="text-secondary mb-3 text-xs">
              {dayjs(detail.receivedAt).format('YYYY-MM-DD HH:mm')}
            </div>
            <iframe
              title={t('body')}
              sandbox=""
              srcDoc={detail.body}
              className="h-80 w-full border-0"
            />
          </>
        )}
      </Modal>
    </>
  );
}
